import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import API_BASE_URL from '../config/api';

const Profile = () => {
  const { user, refreshUser } = useAuth();
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Name editing states
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(user?.name || '');
  const [nameLoading, setNameLoading] = useState(false);
  const [nameError, setNameError] = useState('');
  const [nameSuccess, setNameSuccess] = useState('');

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  // Initialize name value when user changes
  React.useEffect(() => {
    if (user?.name) {
      setNameValue(user.name);
    }
  }, [user?.name]);

  const handleNameEdit = () => {
    setIsEditingName(true);
    setNameError('');
    setNameSuccess('');
  };

  const handleNameCancel = () => {
    setIsEditingName(false);
    setNameValue(user.name);
    setNameError('');
  };

  const handleNameSave = async () => {
    setNameError('');
    setNameSuccess('');
    setNameLoading(true);

    // Validation
    if (!nameValue || nameValue.trim().length < 2) {
      setNameError('Name must be at least 2 characters long');
      setNameLoading(false);
      return;
    }

    if (nameValue.trim().length > 100) {
      setNameError('Name cannot be longer than 100 characters');
      setNameLoading(false);
      return;
    }

    try {
      const response = await axios.put(`${API_BASE_URL}/api/auth/update-profile`, {
        name: nameValue.trim(),
      });

      setNameSuccess('Name updated successfully!');
      setIsEditingName(false);
      
      // Refresh user data to get updated information
      await refreshUser();
      
      setTimeout(() => setNameSuccess(''), 3000);
    } catch (error) {
      setNameError(error.response?.data?.message || 'Failed to update name');
    } finally {
      setNameLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const { currentPassword, newPassword, confirmPassword } = passwordData;

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all password fields');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (newPassword === currentPassword) {
      setError('New password must be different from current password');
      setLoading(false);
      return;
    }

    try {
      await axios.put(`${API_BASE_URL}/api/auth/change-password`, {
        currentPassword,
        newPassword,
      });

      setSuccess('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      
      // Refresh user data
      await refreshUser();
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super-admin':
        return '#9c27b0';
      case 'admin':
        return '#f44336';
      case 'grader':
        return '#ff9800';
      case 'student':
        return '#4caf50';
      default:
        return '#666';
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'super-admin':
        return '🛡️ Super Administrator';
      case 'admin':
        return '👨‍💼 Administrator';
      case 'grader':
        return '🎓 Grader';
      case 'teacher':
        return '👩‍🏫 Teacher';
      case 'student':
        return '📚 Student';
      default:
        return role;
    }
  };

  if (!user) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          Loading profile...
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <h1>👤 My Profile</h1>

      {/* Profile Information */}
      <div className="card">
        <h2>Profile Information</h2>
        <div className="grid grid-2">
          <div>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                type="email"
                value={user.email}
                className="form-control"
                disabled
                style={{ backgroundColor: '#f8f9fa' }}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Full Name</label>
              {nameError && (
                <div className="alert alert-error" style={{ marginBottom: '8px', fontSize: '14px' }}>
                  {nameError}
                </div>
              )}
              {nameSuccess && (
                <div className="alert alert-success" style={{ marginBottom: '8px', fontSize: '14px' }}>
                  {nameSuccess}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="text"
                  value={isEditingName ? nameValue : user.name}
                  onChange={(e) => setNameValue(e.target.value)}
                  className="form-control"
                  disabled={!isEditingName}
                  style={{ 
                    backgroundColor: isEditingName ? '#fff' : '#f8f9fa',
                    flex: 1
                  }}
                  placeholder="Enter your full name"
                />
                {!isEditingName ? (
                  <button
                    type="button"
                    onClick={handleNameEdit}
                    className="btn btn-outline"
                    style={{ fontSize: '12px', padding: '6px 12px' }}
                  >
                    ✏️ Edit
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      type="button"
                      onClick={handleNameSave}
                      disabled={nameLoading}
                      className="btn btn-success"
                      style={{ fontSize: '12px', padding: '6px 12px' }}
                    >
                      {nameLoading ? '💾...' : '💾 Save'}
                    </button>
                    <button
                      type="button"
                      onClick={handleNameCancel}
                      disabled={nameLoading}
                      className="btn btn-secondary"
                      style={{ fontSize: '12px', padding: '6px 12px' }}
                    >
                      ❌ Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <div className="form-group">
              <label className="form-label">Student ID</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="text"
                  value={user.studentId || 'Not assigned'}
                  className="form-control"
                  disabled
                  style={{ 
                    backgroundColor: '#e7f3ff', 
                    fontWeight: 'bold',
                    fontSize: '18px',
                    color: '#0066cc'
                  }}
                />
                <div style={{
                  padding: '8px 12px',
                  backgroundColor: '#e7f3ff',
                  border: '1px solid #b3d9ff',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#0066cc'
                }}>
                  <strong>📋 Your unique identifier</strong>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Role</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span
                  className="badge"
                  style={{
                    backgroundColor: getRoleBadgeColor(user.role),
                    color: 'white',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {getRoleDisplayName(user.role)}
                </span>
                <span className="badge badge-success">
                  {user.status || 'Active'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div style={{ 
          marginTop: '20px', 
          padding: '16px', 
          backgroundColor: '#d1ecf1', 
          borderRadius: '8px', 
          border: '1px solid #bee5eb',
          fontSize: '14px',
          color: '#0c5460'
        }}>
          <strong>ℹ️ Note:</strong> Student ID is auto-assigned and can not be changed, it serves as an unique identifier for each account.
        </div>
      </div>

      {/* Change Password */}
      <div className="card">
        <h2>🔒 Change Password</h2>
        
        {error && (
          <div className="alert alert-error" style={{ marginBottom: '20px' }}>
            {error}
          </div>
        )}
        
        {success && (
          <div className="alert alert-success" style={{ marginBottom: '20px' }}>
            {success}
          </div>
        )}

        <form onSubmit={handlePasswordSubmit}>
          <div className="form-group">
            <label className="form-label">Current Password</label>
            <input
              type="password"
              name="currentPassword"
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
              className="form-control"
              placeholder="Enter your current password"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">New Password</label>
            <input
              type="password"
              name="newPassword"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              className="form-control"
              placeholder="Enter your new password"
              required
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              Must be at least 6 characters long
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              className="form-control"
              placeholder="Confirm your new password"
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
            style={{ marginTop: '16px' }}
          >
            {loading ? (
              <span>
                <div className="spinner" style={{ 
                  width: '16px', 
                  height: '16px', 
                  display: 'inline-block', 
                  marginRight: '8px' 
                }}></div>
                Changing Password...
              </span>
            ) : (
              'Change Password'
            )}
          </button>
        </form>
      </div>

      {/* Account Information */}
      <div className="card">
        <h2>📊 Account Information</h2>
        <div className="grid grid-2">
          <div>
            <h4 style={{ color: '#28a745', marginBottom: '12px' }}>✅ Account Details</h4>
            <ul style={{ paddingLeft: '20px', lineHeight: '1.6', color: '#666' }}>
              <li><strong>Member since:</strong> {new Date(user.createdAt).toLocaleDateString()}</li>
              <li><strong>Last updated:</strong> {new Date(user.updatedAt).toLocaleDateString()}</li>
              <li><strong>Account status:</strong> {user.status || 'Active'}</li>
              <li><strong>Student ID:</strong> {user.studentId}</li>
            </ul>
          </div>
          
          <div>
            <h4 style={{ color: '#0066cc', marginBottom: '12px' }}>🔐 Security</h4>
            <ul style={{ paddingLeft: '20px', lineHeight: '1.6', color: '#666' }}>
              <li>Password protection enabled</li>
              <li>Secure session management</li>
              <li>Role-based access control</li>
              <li>Account activity monitoring</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
